import React, { useState, useEffect, useRef } from 'react';

import { format } from 'date-fns';
import { Modal } from 'flowbite';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import EventCloneModal from './EventCloneModal';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';

import FildeDuplicateIcon from 'Assets/icons/file-duplicate.svg';

const EventsCloneSchema = Yup.object().shape({
    name: Yup.string().required('This value is required'),
    keepFinancialInfo: Yup.boolean().required('This value is required'),
    keyContactId: Yup.number().integer().positive().required(),
    startDate: Yup.date().required('This value is required'),
});

export default function EventsCloneForm(props) {
    const [eventList, setEventList] = useState([]);
    const [budgetList, setBudgetList] = useState([]);
    const [eventId, setEventId] = useState('');
    const [budgetId, setBudgetId] = useState('');
    const [request, setRequest] = useState(false);
    const [users, setUsers] = useState([]);

    const [eventCloneModalElement, setEventCloneModalElement] = useState(null);

    const eventCloneModalRef = useRef();

    useEffect(() => {
        fetchEvents();
        eventCloneModalRef && setEventCloneModalElement(new Modal(eventCloneModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        budgetsForEvent();
    }, [eventId]);

    const fetchEvents = () => {
        axios
            .get(props.eventsListUrl)
            .then((response) => {
                const { data } = response;
                setEventList(data.events);
                setUsers(data.users);
                if (data.events.length > 0) {
                    const firstEvent = data.events[0];
                    setEventId(firstEvent.id);
                    setDefaultBudgetId(firstEvent);
                }
            })
            .catch((e) => {
                console.log(e.response);
            });
    };

    const budgetsForEvent = () => {
        const event = eventList.find((item) => {
            return parseInt(item.id) === parseInt(eventId);
        });

        const budgets = event ? [...event.budgets] : [];
        setBudgetList(budgets);

        if (event) {
            setDefaultBudgetId(event);
        }
    };

    const setDefaultBudgetId = (event) => {
        if (event) {
            const budget = event.budgets.find((item) => {
                return !item.is_locked;
            });
            if (budget) {
                setBudgetId(budget.id);
            }
        } else {
            setBudgetId('');
        }
    };

    const changeEvent = (e) => {
        setEventId(parseInt(e.target.value));
    };

    const changeBudget = (e) => {
        setBudgetId(parseInt(e.target.value));
    };

    const closeHandler = () => {
        window.location.href = props.redirectUrl;
    };

    return (
        <Formik
            initialValues={{
                name: '',
                keepFinancialInfo: 1,
                keyContactId: '',
                startDate: '',
            }}
            validationSchema={EventsCloneSchema}
            onSubmit={(values, actions) => {
                setRequest(true);
                let data = { ...values };
                data.eventId = eventId;
                data.budgetId = budgetId;
                data.startDate = format(new Date(values.startDate), 'yyyy-MM-dd');

                axios
                    .post(props.eventCloneUrl, data)
                    .then((response) => {
                        eventCloneModalElement && eventCloneModalElement.show();
                    })
                    .catch((e) => {
                        actions.setErrors(e.response.data.errors);
                    })
                    .finally(() => {
                        setRequest(false);
                        actions.setSubmitting(false);
                    });
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <div className="row">
                    <div className="col-sm-6 offset-sm-3 col-md-4 offset-md-4">
                        <div className="card bg-white border-0 rounded-15px py-2 shadow">
                            <div className="card-body">
                                <div className="form-group">
                                    <label htmlFor="ame">
                                        Project Name <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        className={errors.name && touched.name ? 'form-control is-invalid border border-danger' : 'form-control'}
                                        name="name"
                                        placeholder="Project Name"
                                    />
                                    {errors.name && touched.name && <div className="text-danger">{errors.name}</div>}
                                </div>

                                {eventList.length > 0 && (
                                    <div className="form-group">
                                        <label htmlFor="event-id">
                                            Which Project is to be cloned <span className="text-danger">*</span>
                                        </label>
                                        <select id="event-id" className="form-control" value={eventId} onChange={changeEvent}>
                                            {eventList.map((event) => {
                                                return (
                                                    <option value={event.id} key={event.slug + event.id}>
                                                        {event.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                )}

                                {eventList.length > 0 && (
                                    <div className="form-group">
                                        <label htmlFor="budget-id">
                                            Select which version of the budget to clone <span className="text-danger">*</span>
                                        </label>
                                        <select id="budget-id" className="form-control" value={budgetId} onChange={changeBudget}>
                                            {budgetList.map((budget) => {
                                                return (
                                                    <option value={budget.id} key={budget.id + '_' + budget.event_id}>
                                                        {budget.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="keep-financial">
                                        Keep financial information <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        as={'select'}
                                        className={
                                            errors.keepFinancialInfo && touched.keepFinancialInfo
                                                ? 'form-control is-invalid border border-danger'
                                                : 'form-control'
                                        }
                                        name="keepFinancialInfo"
                                    >
                                        <option value={1}>Yes</option>
                                        <option value={0}>No</option>
                                    </Field>
                                </div>

                                <div className={'form-group'}>
                                    <label htmlFor="ticketing">
                                        Project Admin <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        as="select"
                                        className={
                                            errors.keyContactId && touched.keyContactId ? 'form-control is-invalid border border-danger' : 'form-control'
                                        }
                                        name="keyContactId"
                                    >
                                        {!values.keyContactId && <option value={null}>Select Admin</option>}
                                        {users.map((user) => {
                                            return (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name}
                                                </option>
                                            );
                                        })}
                                    </Field>
                                    {errors.keyContactId && touched.keyContactId && <div className="text-danger">{errors.keyContactId}</div>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="startDate">
                                        Project Start Date <span className="text-danger">*</span>
                                    </label>
                                    <DatePickerField
                                        name="startDate"
                                        className={errors.startDate && touched.startDate ? 'form-control is-invalid border border-danger' : 'form-control'}
                                        value={values.startDate}
                                        onChange={setFieldValue}
                                    />
                                    {errors.startDate && touched.startDate && <div className="text-danger">{errors.startDate}</div>}
                                </div>

                                <div className="flex justify-content-end">
                                    <Button variant="primary" disabled={isSubmitting} onClick={handleSubmit}>
                                        {request ? (
                                            <i className="fa fa-spinner fa-spin " aria-hidden="true"></i>
                                        ) : (
                                            <FildeDuplicateIcon className="tw-w-3 tw-h-3 tw-mr-1" aria-hidden="true" />
                                        )}
                                        Submit
                                    </Button>
                                </div>

                                {request && (
                                    <div className="alert alert-primary my-4" role="alert">
                                        The project cloning process is in progress. Please wait for it to complete. This may take several minutes.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <EventCloneModal
                        modalId={'clone-event-modal'}
                        modalRef={eventCloneModalRef}
                        modalElement={eventCloneModalElement}
                        closeHandler={closeHandler}
                    />
                </div>
            )}
        </Formik>
    );
}
